import { useEffect, useMemo, type ReactElement } from 'react'
import { useSearchParams } from 'react-router-dom'
import { StationCardView, StationCardLoading, type StatusBannerVariant } from '@metro/station-card'
import styles from './StationCardVisualFixture.module.css'

/**
 * Private QA fixture for Playwright phone-overlay snapshots (Figma
 * StationCard V2 spec §7.3). Mounts a single card variant inside a
 * fixed iPhone-16-Pro-sized frame (402 × 874) at the spec's vertical
 * anchor — compact 2026-07 (equal-48 buttons): card bottom pinned at y=850,
 * so y=644 for 206-tall states, y=572/580 for 278/270-tall sandwich states —
 * so every Playwright screenshot lands pixel-stable.
 *
 * URL contract:
 *   /__visual/stationcard?state=<name>&theme=<light|dark>
 *
 * `state` is one of:
 *   normal · long-name · no-zone · not-found · loading ·
 *   closed · restricted
 *
 * `theme` defaults to `light`. Setting `theme=dark` flips
 * `prefers-color-scheme` for the page-level `:root` token block via a
 * `data-theme="dark"` attribute on `<html>` — the page intentionally
 * does NOT inherit the user's OS preference here so the same fixture
 * deterministically renders both themes from a query param. Playwright
 * also drives the `colorScheme` via `browser.newContext`, which is
 * redundant but defensive.
 *
 * This route is dev/QA only and excluded from search-engine indexing
 * via `public/robots.txt`'s `Disallow: /__visual/`.
 */

type FixtureState =
  | 'normal'
  | 'long-name'
  | 'no-zone'
  | 'not-found'
  | 'loading'
  | 'closed'
  | 'restricted'

const STATIONS: Record<
  FixtureState,
  {
    id: string
    name: string
    lineLocalName: string
    lineHexColor: string
    zone: string | null
  }
> = {
  normal: {
    id: 'baker-street',
    name: 'Baker Street',
    lineLocalName: 'Bakerloo line',
    lineHexColor: '#B36305',
    zone: '1',
  },
  'long-name': {
    id: 'shepherds-bush-market',
    name: "Shepherd's Bush Market",
    lineLocalName: 'Hammersmith & City line',
    lineHexColor: '#F4A9BE',
    zone: '2',
  },
  'no-zone': {
    id: 'canary-wharf',
    name: 'Canary Wharf',
    lineLocalName: 'Elizabeth line',
    lineHexColor: '#6950A1',
    zone: null,
  },
  'not-found': {
    id: 'unknown',
    name: '',
    lineLocalName: '',
    lineHexColor: '#000000',
    zone: null,
  },
  loading: {
    id: 'loading',
    name: '',
    lineLocalName: '',
    lineHexColor: '#000000',
    zone: null,
  },
  closed: {
    id: 'kings-cross',
    name: "King's Cross St. Pancras",
    lineLocalName: 'Northern line',
    lineHexColor: '#000000',
    zone: '1',
  },
  restricted: {
    id: 'baker-street',
    name: 'Baker Street',
    lineLocalName: 'Jubilee line',
    lineHexColor: '#A0A5A9',
    zone: '1',
  },
}

const VALID_STATES: ReadonlySet<FixtureState> = new Set<FixtureState>([
  'normal',
  'long-name',
  'no-zone',
  'not-found',
  'loading',
  'closed',
  'restricted',
])

function parseState(raw: string | null): FixtureState {
  if (raw !== null && (VALID_STATES as Set<string>).has(raw)) {
    return raw as FixtureState
  }
  return 'normal'
}

export function StationCardVisualFixture(): ReactElement {
  const [params] = useSearchParams()
  const state = parseState(params.get('state'))
  const theme = params.get('theme') === 'dark' ? 'dark' : 'light'

  // Pin the document theme so Playwright's `colorScheme` context and
  // the explicit query param converge on one deterministic palette.
  useEffect(() => {
    const html = document.documentElement
    const prev = html.dataset.theme
    html.dataset.theme = theme
    // Hide the rest of the app chrome to keep the screenshot focused.
    html.classList.add('visual-fixture-active')
    return () => {
      if (prev === undefined) {
        delete html.dataset.theme
      } else {
        html.dataset.theme = prev
      }
      html.classList.remove('visual-fixture-active')
    }
  }, [theme])

  const cardSlot = useMemo<ReactElement>(() => renderCardSlot(state), [state])

  // Top-anchor coordinate — the card's BOTTOM edge stays pinned at y=850
  // (unchanged bottom-overlay position); the top is derived from the compact
  // 2026-07 heights (each variant is 18px shorter than before):
  //   206-tall states → y = 644 (Normal · Long name · No zone · Not found · Loading)
  //   278-tall Closed → y = 572
  //   270-tall Restricted → y = 580 (warning sandwich is 8pt shorter)
  const top = state === 'closed' ? 572 : state === 'restricted' ? 580 : 644
  const cardHeight = state === 'closed' ? 278 : state === 'restricted' ? 270 : 206

  return (
    <div
      className={styles.phone}
      data-testid="stationcard-visual-fixture"
      data-state={state}
      data-theme={theme}
    >
      <div
        className={styles.cardAnchor}
        style={{ top: `${top}px`, width: '354px', height: `${cardHeight}px` }}
      >
        {cardSlot}
      </div>
    </div>
  )
}

function renderCardSlot(state: FixtureState): ReactElement {
  switch (state) {
    case 'loading':
      return <StationCardLoading />
    case 'not-found':
      // Re-use the not-found surface inline so the fixture controls
      // the card-shaped chrome at a fixed 354 × 244 box; production
      // mounts this via `<StationCard cards={[]} />`, but the
      // fixture is single-card by design.
      return <StationCardNotFoundCard />
    case 'closed':
    case 'restricted':
      return (
        <StationCardView
          station={STATIONS[state]}
          status={state as StatusBannerVariant}
          onFromTap={noopWithId}
          onToTap={noopWithId}
          onDetailsTap={noop}
          onStatusBannerTap={noopWithId}
        />
      )
    case 'normal':
    case 'long-name':
    case 'no-zone':
      return (
        <StationCardView
          station={STATIONS[state]}
          onFromTap={noopWithId}
          onToTap={noopWithId}
          onDetailsTap={noop}
        />
      )
  }
}

function noopWithId(_id: string): void {
  /* fixture only */
}
function noop(): void {
  /* fixture only */
}

/**
 * Inline not-found card body. Mirrors `StationCard.tsx`'s empty-state
 * branch (magnifier 56px / boldPx20 / Retry h64) at a fixed 354 × 244
 * box so the fixture renders identically to a single carousel slot.
 */
function StationCardNotFoundCard(): ReactElement {
  return (
    <article className={styles.notFound} data-testid="station-card-not-found">
      <NotFoundMagnifier />
      <h2 className={styles.notFoundTitle}>Station not found</h2>
      <button type="button" className={styles.notFoundRetry}>
        Retry
      </button>
    </article>
  )
}

function NotFoundMagnifier(): ReactElement {
  return (
    <svg
      className={styles.notFoundIcon}
      viewBox="0 0 56 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M24 38C31.732 38 38 31.732 38 24C38 16.268 31.732 10 24 10C16.268 10 10 16.268 10 24C10 31.732 16.268 38 24 38Z"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path d="M34 34L48 48" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  )
}

export default StationCardVisualFixture
